// src/services/authService.ts
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

// Types
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  accountType?: string;
  createdAt?: string;
  profilePictureUrl?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

// Auth service class
class AuthService {
  // Store tokens in localStorage
  private storeTokens(token: string): void {
    localStorage.setItem('accessToken', token);
  }

  // Get stored tokens
  public getTokens(): AuthTokens | null {
    const accessToken = localStorage.getItem('accessToken');
    
    if (accessToken) {
      return { accessToken };
    }
    
    return null;
  }

  // Clear tokens on logout
  private clearTokens(): void {
    localStorage.removeItem('accessToken');
  }

  // Login user
public async login(credentials: LoginCredentials): Promise<User> {
  try {
    const response = await axios.post(API_ENDPOINTS.LOGIN, credentials);
    const { user, token } = response.data;
    
    this.storeTokens(token);
    return user;
  } catch (error: any) {
    // Handle and standardize error format
    const errorMessage = error.response?.data?.error || 'Login failed. Please try again.';
    throw new Error(errorMessage);
  }
}

  // Register user
  public async register(userData: RegisterData): Promise<User> {
    try {
      const response = await axios.post(API_ENDPOINTS.REGISTER, userData);
      const { user, token } = response.data;
      
      this.storeTokens(token);
      return user;
    } catch (error) {
      throw error;
    }
  }

  // Logout user
  public logout(): void {
    this.clearTokens();
  }

  // Check if user is authenticated
  public isAuthenticated(): boolean {
    return !!this.getTokens()?.accessToken;
  }

  // Request password reset
  public async forgotPassword(email: string): Promise<void> {
    try {
      await axios.post(API_ENDPOINTS.FORGOT_PASSWORD, { email });
    } catch (error) {
      throw error;
    }
  }

  // Reset password with token
  public async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      await axios.post(API_ENDPOINTS.RESET_PASSWORD, {
        token,
        newPassword
      });
    } catch (error) {
      throw error;
    }
  }

  // Refresh access token
  public async refreshToken(): Promise<string> {
    try {
      const tokens = this.getTokens();
      
      if (!tokens?.accessToken) {
        throw new Error('No access token available');
      }
      
      const response = await axios.post(API_ENDPOINTS.REFRESH_TOKEN, {
        token: tokens.accessToken
      });
      
      const newToken = response.data.token;
      this.storeTokens(newToken);
      
      return newToken;
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  }
}

// Create and export auth service instance
const authService = new AuthService();
export default authService;